import React from 'react';
import { FiAlertTriangle, FiX, FiClock } from 'react-icons/fi';

const SessionConsentModal = ({ sessions, onConfirm, onCancel, loading }) => {
  
  // Format the date to look clean (e.g., "Oct 14, 02:30 PM")
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Choose the right emoji based on the operating system
  const getDeviceIcon = (platform) => {
    const p = platform?.toLowerCase() || '';
    if (p.includes('windows')) return '🖥️';
    if (p.includes('mac')) return '🍎';
    if (p.includes('android')) return '📱';
    if (p.includes('ios') || p.includes('iphone')) return '📱';
    if (p.includes('linux')) return '🐧';
    return '💻';
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-gray-900/60 backdrop-blur-sm p-0 sm:p-4 animate-fade-in">
      
      {/* Background overlay click to close */}
      <div className="absolute inset-0" onClick={() => !loading && onCancel()} />

      {/* Modal Box */}
      <div className="relative bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 z-10 max-h-[95vh] flex flex-col border border-gray-100 animate-slide-up">
        
        {/* Top Accent Bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-amber-500 to-red-500 shrink-0" />
        
        {/* Close Button */}
        <button 
          onClick={onCancel}
          disabled={loading}
          className="absolute right-4 top-5 p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors z-20 disabled:opacity-40"
          aria-label="Close modal"
        >
          <FiX className="w-5 h-5" />
        </button>

        {/* Content Container */}
        <div className="overflow-y-auto p-6 space-y-5">
          
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-3 mt-2">
            <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl w-fit">
              <FiAlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Active Session Detected</h3>
              <p className="text-sm text-gray-500 mt-0.5">You are already logged in on another device.</p>
            </div>
          </div>

          {/* Active Sessions List Panel */}
          <div className="bg-gray-50/70 border border-gray-100 rounded-xl p-4 space-y-3 max-h-48 overflow-y-auto">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              {sessions.length} Active Session(s):
            </p>
            
            {sessions.map((session, index) => (
              <div key={index} className="flex items-start gap-3 text-sm border-b border-gray-200/60 pb-3 last:border-0 last:pb-0">
                <span className="text-xl flex-shrink-0 mt-0.5" aria-hidden="true">
                  {getDeviceIcon(session.deviceInfo?.platform)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 truncate">
                    {session.deviceInfo?.platform || 'Unknown Device'}
                  </p>
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {session.deviceInfo?.browser || 'Unknown Browser'} • IP: {session.deviceInfo?.ipAddress || 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                    <FiClock className="w-3.5 h-3.5" />
                    Last active: {formatDate(session.lastActive)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Action Message Warning */}
          <p className="text-sm text-gray-600 text-center sm:text-left leading-relaxed">
            If you continue, you will be logged out from <strong>all other devices</strong> immediately.
          </p>

          {/* Form Action Controls */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-1">
            <button
              onClick={onCancel}
              disabled={loading}
              className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancel Login
            </button>
            
            <button
              onClick={onConfirm}
              disabled={loading}
              className="w-full sm:w-auto px-5 py-2.5 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition-colors flex items-center justify-center gap-2 shadow-sm shadow-amber-100"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Please wait...
                </>
              ) : (
                'Continue & Log Out Others'
              )}
            </button>
          </div>
          
        </div>
      </div>

      {/* Mobile Fluid Animation CSS Frames */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @media (min-width: 640px) {
          @keyframes slideUp {
            from { transform: scale(0.96); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.15s linear forwards;
        }
        .animate-slide-up {
          animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
};

export default SessionConsentModal;